import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GetServersResponse, Server } from '@4players/fleet';
import {
  catchError,
  concatMap,
  filter,
  from,
  map,
  mergeMap,
  of,
  range,
  reduce,
  switchMap,
  take,
} from 'rxjs';
import { App, hasActivatedProduct } from 'app/core/app/app.model';
import { AppService } from 'app/core/app/app.service';
import { GameserverService } from 'app/modules/admin/fleet/gameserver.service';

/** Servers fetched per request. The API pages; this is the page size. */
const SERVERS_PER_APP = 100;

/**
 * How many pages are followed per project, i.e. a ceiling of five hundred
 * servers each.
 *
 * The palette can only search what it holds, and page one alone left a project
 * with two hundred servers half unreachable without saying so. There is no
 * cross-project server search on the Fleet API to defer to, so the choice is a
 * ceiling or an unbounded fetch — and the extra pages are only ever requested
 * by the projects that actually have them, after every project's first page is
 * already in.
 */
const MAX_PAGES = 5;

/** How long an indexed project counts as current before a reopen refetches it. */
const FRESH_MS = 60_000;

/**
 * How long the index as a whole counts as current before a search rebuilds it.
 *
 * Only the *active* project was ever refreshed, so a project the user did not
 * visit kept its sign-in snapshot for the whole session — and a fleet that
 * scales is a fleet whose servers are mostly younger than that. Searching is
 * the moment the index has to be right, and the moment the user has said they
 * are looking for something, so that is what pays for the round.
 *
 * It costs nothing at the shortcut: rows come from what is already held and the
 * list updates as answers land. Merely opening the palette still fans out to
 * nothing.
 */
const REFRESH_ALL_MS = 5 * 60_000;

/** Projects fetched at once. Enough to fill quickly, few enough not to burst. */
const CONCURRENCY = 3;

/**
 * Every project's servers, kept warm so the palette can offer them instantly.
 *
 * Quick Access reaches every page of every project, and a server detail page is
 * four steps away without it — Fleet, Servers, find the row, open it. Offering
 * the servers themselves only works if the palette already holds them: loading
 * on open would put a spinner between the shortcut and the answer, which is the
 * one thing that shortcut cannot afford.
 *
 * So the index fills **while the palette is closed**, which is also why this is
 * a service rather than state on the component: the component does not exist
 * until the palette is opened for the first time.
 *
 * ## Which projects are indexed
 *
 * Only the ones **Fleet itself lists**, never every project the console shows.
 * The two are not the same set and nothing in a console project says which it
 * is in: on a real account, all eighty-three projects carried `fleet` in
 * `activatedProducts`, while Fleet granted twenty-four. Indexing the console's
 * list produced fifty-nine requests, thirty-seven of them structurally doomed —
 * 404 for a Fleet app that does not exist, 403 for one this session may not
 * read — and, because a failure recorded nothing, the same doomed requests went
 * out again on the next trigger.
 *
 * `GameserverService.apps$` is that list, loaded once and shared with the rest
 * of the Fleet module, so the answer usually costs nothing.
 *
 * ## What it costs
 *
 * One request per *Fleet* project, once per session, three at a time, in the
 * background after both lists have arrived. After that, one request when a
 * project becomes active, and at most one when the palette opens on a project
 * whose entry has gone stale. **Opening never fans out** — that is the rule
 * that keeps a thirteen-project account from firing thirteen requests at the
 * moment someone hits the shortcut.
 *
 * Reads go through `GameserverService`, which shares in-flight and recent
 * results, so visiting a project's server list warms this index for free and
 * vice versa.
 *
 * A project whose request fails is simply absent, and is not asked again this
 * session. An index that cannot be built must never surface as an error — nor
 * as traffic: the pages it would have offered are all still reachable the long
 * way.
 */
@Injectable({ providedIn: 'root' })
export class FleetServerIndexService {
  private readonly _appService = inject(AppService);
  private readonly _gameserverService = inject(GameserverService);

  private readonly _servers = signal<ReadonlyMap<string, Server[]>>(new Map());

  /** When each project was last indexed, for {@link ensureFresh}. */
  private readonly _loadedAt = new Map<string, number>();

  /** Projects with a request out, so a second trigger does not duplicate it. */
  private readonly _inFlight = new Set<string>();

  /**
   * Projects whose request failed, never asked again this session.
   *
   * Without this a failure left no trace, so every later trigger repeated it —
   * which is how one sign-in produced two 403s for the same project.
   */
  private readonly _failed = new Set<string>();

  /** Fleet app ids this session can actually read. */
  private _fleetAppIds: ReadonlySet<number> = new Set();

  /** The latest project list, for {@link refreshAll}. */
  private _apps: App[] = [];

  /** When the whole index was last rebuilt, for {@link refreshAll}. */
  private _refreshedAllAt = 0;

  /** Indexed servers per project id. Empty until the first answers arrive. */
  readonly servers = this._servers.asReadonly();

  constructor() {
    // The project list is the starting gun: it means the console has booted and
    // the user is looking at something, which is exactly when there is room to
    // fetch in the background.
    // The console's project list: what there is to offer.
    this._appService.apps$.pipe(takeUntilDestroyed()).subscribe((apps) => {
      this._apps = apps ?? [];
    });

    // Fleet's own list: which of those it will answer for.
    //
    // Read only once a project list has arrived, and never at construction.
    // Reading `apps$` is what triggers its first load, and this service is
    // built with the console shell — on the sign-in page, before there is a
    // Fusion session. That load then fails with `No Fusion Session ID
    // available` and is never retried, because the getter marks itself
    // initialised either way: one careless read here would leave the whole
    // Fleet module without its app list.
    this._appService.apps$
      .pipe(
        filter((apps) => !!apps?.length),
        take(1),
        switchMap(() => this._gameserverService.apps$),
        map((apps) => new Set(apps.map((app) => Number(app.id)))),
        // The subject starts empty and fills in. An empty set is also the right
        // answer for an account with no Fleet projects — either way there is
        // nothing to index yet.
        filter((ids) => ids.size > 0),
        takeUntilDestroyed(),
      )
      .subscribe((fleetAppIds) => {
        this._fleetAppIds = fleetAppIds;
        this._refreshedAllAt = Date.now();
        this._primeAll(this._apps);
      });

    // Being in a project is the strongest hint that its servers are the ones
    // about to be searched — and the moment its list is most likely to have
    // changed since the index was built.
    // Not an unconditional refetch: `activeApp$` re-emits on restore as well as
    // on a real switch, and refetching every time would undo the prefetch it
    // was meant to top up. Anything younger than `FRESH_MS` is left alone.
    this._appService.activeApp$
      .pipe(takeUntilDestroyed())
      .subscribe((app) => this.ensureFresh(app));
  }

  /**
   * Refresh one project if its entry has aged past {@link FRESH_MS}.
   *
   * Called when the palette opens, for the active project only. Deliberately
   * not for every project: a reopen must stay free.
   */
  /**
   * Rebuild every project's index, unless that was already done recently.
   *
   * Called when the user starts typing — see {@link REFRESH_ALL_MS} for why the
   * first keystroke rather than the opening — and guarded by that interval, so
   * calling it per keystroke costs a clock read.
   */
  refreshAll(): void {
    if (Date.now() - this._refreshedAllAt < REFRESH_ALL_MS) return;

    this._refreshedAllAt = Date.now();
    this._primeAll(this._apps, true);
  }

  ensureFresh(app: App | null): void {
    if (!app) return;

    const loadedAt = this._loadedAt.get(app.id);

    if (loadedAt && Date.now() - loadedAt < FRESH_MS) return;

    this._load(app);
  }

  /**
   * Index every Fleet project, the active one first.
   *
   * Order matters more than it looks: the first result to land is the one the
   * user is most likely to search, and the rest arrive while they are still
   * reading it.
   */
  private _primeAll(apps: App[], force = false): void {
    const activeId = this._appService.activeApp?.id;
    const pending = apps
      .filter((app) => this._isIndexable(app))
      .filter(
        (app) =>
          (force || !this._loadedAt.has(app.id)) && !this._inFlight.has(app.id),
      )
      .sort((a, b) => Number(b.id === activeId) - Number(a.id === activeId));

    if (!pending.length) return;

    // Claim them all now, not when each request starts. `mergeMap` holds the
    // queue back to `CONCURRENCY`, so with the claim inside `_request` the apps
    // still waiting their turn look untouched — and a second emission of the
    // project list queues every one of them again. That is how thirteen
    // projects produced twenty-seven requests at six at a time.
    for (const app of pending) this._inFlight.add(app.id);

    // No teardown: every request completes on its own, and this service is
    // provided in root — it outlives every component that reads it.
    from(pending)
      .pipe(mergeMap((app) => this._request(app), CONCURRENCY))
      .subscribe();
  }

  private _load(app: App): void {
    if (!this._isIndexable(app)) return;
    if (this._inFlight.has(app.id)) return;

    this._inFlight.add(app.id);
    this._request(app).subscribe();
  }

  /**
   * Whether this project is one Fleet will answer for.
   *
   * `activatedProducts` is not that answer — it said yes for every project on
   * an account where Fleet knew a quarter of them. Fleet's own list is.
   */
  private _isIndexable(app: App): boolean {
    const fleetAppId = Number.parseInt(app.appId ?? '', 10);

    return (
      hasActivatedProduct(app, 'fleet') &&
      !!fleetAppId &&
      this._fleetAppIds.has(fleetAppId) &&
      !this._failed.has(app.id)
    );
  }

  private _request(app: App) {
    const fleetAppId = Number.parseInt(app.appId ?? '', 10);

    if (!fleetAppId) {
      this._inFlight.delete(app.id);
      return of(null);
    }

    return this._page(fleetAppId, 1).pipe(
      mergeMap((first) => (first ? this._restOf(fleetAppId, first) : of(null))),
      // `tap` would run before `catchError`'s replacement; doing the
      // bookkeeping in `mergeMap` keeps success and failure on one path.
      mergeMap((servers) => {
        this._inFlight.delete(app.id);

        if (!servers) {
          this._failed.add(app.id);
        }

        if (servers) {
          this._loadedAt.set(app.id, Date.now());
          this._servers.update((current) => {
            const next = new Map(current);
            next.set(app.id, servers);
            return next;
          });
        }

        return of(servers);
      }),
    );
  }

  /**
   * The first page plus however many more the project has, up to
   * {@link MAX_PAGES}.
   *
   * Sequential rather than parallel: a project deep enough to need this is
   * exactly the one whose account is least helped by four more simultaneous
   * requests, and nothing is waiting on the result.
   */
  private _restOf(fleetAppId: number, first: GetServersResponse) {
    const firstPage = first.data ?? [];
    const lastPage = Math.min(first.meta?.last_page ?? 1, MAX_PAGES);

    if (lastPage <= 1) return of(firstPage);

    return range(2, lastPage - 1).pipe(
      concatMap((page) => this._page(fleetAppId, page)),
      reduce(
        (all, response) => all.concat(response?.data ?? []),
        [...firstPage],
      ),
    );
  }

  /** One page, or `null` when it fails — a partial index beats none. */
  private _page(fleetAppId: number, page: number) {
    return this._gameserverService
      .getAppServers(fleetAppId, SERVERS_PER_APP, page)
      .pipe(catchError(() => of(null)));
  }
}
